import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subject, timer } from 'rxjs';
import { debounce, distinctUntilChanged, filter, publishReplay, refCount, take, takeUntil } from 'rxjs/operators';
import { FadeInOut } from '../../../../animations/fade-in-out.animation';
import { DLEntry } from '../../../shared/models/description-list-entry.model';
import { DomService } from '../../../shared/services/dom.service';
import { ConfirmIncomeChangeModalComponent } from '../../components/confirm-income-change-modal/confirm-income-change-modal.component';
import { EmploymentStatus, IncomeChangeModalData } from '../../models/income.model';
import { AuthFormFieldName, AuthFormName, AuthLinkType } from '../../../../enums/event/event-data.enum';
import { FormTracking } from '../../../../config/eev-form-tracking.config';
import { FieldError } from '../../../../models/event-data.model';

const employmentStatusMapper: Record<EmploymentStatus, DLEntry<EmploymentStatus>> = {
  Unknown: { label: 'Unknown', value: 'Unknown' },
  SalariedEmployee: { label: 'Salaried Employee', value: 'SalariedEmployee' },
  HourlyWageEmployee: { label: 'Hourly Wage Employee', value: 'HourlyWageEmployee' },
  SelfEmployed: { label: 'Self Employed', value: 'SelfEmployed' },
  Contractor: { label: 'Contractor', value: 'Contractor' },
  Student: { label: 'Student', value: 'Student' },
  Retired: { label: 'Retired', value: 'Retired' },
  Unemployed: { label: 'Unemployed', value: 'Unemployed' },
  Other: { label: 'Other', value: 'Other' },
};

@Component({
    selector: 'app-gross-income-edit-mode',
    templateUrl: './gross-income-edit-mode.component.html',
    styleUrls: ['./gross-income-edit-mode.component.scss'],
    animations: [FadeInOut(200, 150)],
    standalone: false
})
export class GrossIncomeEditModeComponent implements OnInit {
  @Input() isNullField: boolean;
  @Input() incomeForm: UntypedFormGroup;
  @Input() employmentStatusOptions: DLEntry<EmploymentStatus>[];
  @Output() onCancel: EventEmitter<any> = new EventEmitter();

  isIndividualGrossAnnualIncomeWarning = false;
  isSuccessfullySubmitted = false;
  isIndividualGrossAnnualIncomeFocused = false;
  isHouseholdIncomeFocused = false;
  AuthFormFieldName = AuthFormFieldName;
  incomeFormTrackingConfig = FormTracking[AuthFormName.PROFILE_INCOME_EDIT];
  AuthLinkType = AuthLinkType;

  public fieldErrors$: Subject<FieldError> = new Subject();
  private readonly _isIndividualGrossAnnualIncomeGroupHovered = new BehaviorSubject(false);
  private readonly _isHouseholdIncomeGroupHovered = new BehaviorSubject(false);
  isIndividualGrossAnnualIncomeGroupHovered = this._isIndividualGrossAnnualIncomeGroupHovered.asObservable().pipe(
    //  debounce when already open, so user has a grace period to hover outside
    debounce((v) => (!v ? timer(500) : timer(0))),
    distinctUntilChanged(),
    publishReplay(1),
    refCount()
  );
  isHouseholdIncomeGroupHovered = this._isHouseholdIncomeGroupHovered.pipe(
    //  debounce when already open, so user has a grace period to hover outside
    debounce((v) => (!v ? timer(500) : timer(0))),
    distinctUntilChanged(),
    publishReplay(1),
    refCount()
  );

  private readonly ngUnsubscribe$: Subject<boolean> = new Subject();
  constructor(private readonly dialog: MatDialog, private readonly domService: DomService) {}

  ngOnInit(): void {
    if (!this.isNullField) {
      this.incomeForm.markAllAsTouched();
    }

    this.incomeForm.controls['individualGrossAnnualIncome'].valueChanges
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((v) => {
        this.isIndividualGrossAnnualIncomeWarning =
          this.incomeForm.controls['individualGrossAnnualIncome'].dirty &&
          this.incomeForm.controls['individualGrossAnnualIncome'].valid &&
          v < 5000;
      });
  }
  ngOnDestroy() {
    this.ngUnsubscribe$.next(null);
    this.ngUnsubscribe$.complete();
  }

  saveIncomeForm() {
    this.incomeForm.markAllAsTouched();
    if (this.incomeForm.invalid) {
      this.domService.scrollToFirstFormError();
      return;
    }

    this.dialog
      .open(ConfirmIncomeChangeModalComponent, {
        data: this.getIncomeChangeModalData(),
        maxWidth: '529px',
        panelClass: 'be-dialog',
      })
      .afterClosed()
      .pipe(
        filter((submitObj) => submitObj && submitObj?.isSuccessfullySubmitted),
        take(1)
      )
      .subscribe(({ isSuccessfullySubmitted }) => {
        window.scrollTo(0, 0);
        this.isSuccessfullySubmitted = isSuccessfullySubmitted;
      });
  }

  getIncomeChangeModalData(): IncomeChangeModalData {
    return {
      profileType: 'income',
      individualGrossAnnualIncome: this.incomeForm.controls['individualGrossAnnualIncome'].value.toString(),
      householdIncome: this.incomeForm.controls['householdIncome'].value.toString(),
      employmentStatus: this.incomeForm.controls['employmentStatus'].value,
    };
  }

  setIsIndividualGrossAnnualIncomeFocused(bool: boolean) {
    this.isIndividualGrossAnnualIncomeFocused = bool;
  }

  setIsHouseholdIncomeFocused(bool: boolean) {
    this.isHouseholdIncomeFocused = bool;
  }

  setIsIndividualGrossAnnualIncomeGroupHovered(bool: boolean) {
    this._isIndividualGrossAnnualIncomeGroupHovered.next(bool);
  }

  setIsHouseholdIncomeGroupHovered(bool: boolean) {
    this._isHouseholdIncomeGroupHovered.next(bool);
  }
}
